import { useMemo } from 'react';
import type { PremiereExportPreset } from '@frameio/graphql-client/generated';
import { useGetPresetsQuery } from '../../../gql/queries/generated/GetPresets';

type PresetOption = {
  value: string;
  label: string;
  group: 'favorite' | 'custom' | undefined;
};

const DEFAULT_LOADING_PRESET: PremiereExportPreset = {
  id: 'default',
  format: 'H.264',
  name: 'Loading Presets',
  isFavorite: false,
  isCustom: false,
};

export const usePresets = (presetId?: string | null) => {
  const {
    data: presetData,
    loading: presetLoading,
    refetch,
  } = useGetPresetsQuery({
    context: {
      resolveWithUXP: true,
    },
  });

  const presets = useMemo(
    () => presetData?.getExportPresets || [DEFAULT_LOADING_PRESET],
    [presetData]
  );
  const presetIds = presetData?.getExportPresets?.map((p) => p?.id) || [];

  let validPresetId = presets?.[0]?.id;
  if (presetId && presetIds.includes(presetId)) {
    validPresetId = presetId;
  }

  const preset = validPresetId
    ? presets?.find((p) => p?.id === validPresetId)
    : undefined;

  const presetOptions = useMemo(
    () =>
      presets.reduce<Record<'favorite' | 'custom', PresetOption[]>>(
        (acc, p) => {
          if (p) {
            let group: PresetOption['group'];
            if (p.isFavorite) {
              group = 'favorite';
            } else if (p.isCustom) {
              group = 'custom';
            }

            if (!group) {
              return acc;
            }

            return {
              ...acc,
              [group]: [
                ...acc[group],
                {
                  value: p.id,
                  label: p.name,
                },
              ],
            };
          }
          return acc;
        },
        { favorite: [], custom: [] }
      ),
    [presets]
  );

  return {
    preset: preset || undefined,
    presetOptions,
    presetLoading,
    refetch,
  };
};
